import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";
import type { LoyaltyPreviewOfferedProduct } from "./preview-scenarios/loyalty-preview.shared";

/** Reward row shown by the Pongo asset list in CB2 preview. */
export type PongoAssetListPreviewAsset = {
  hash: string;
  id: string;
  type:
    | "voucher_discount_amount"
    | "voucher_discount_percent"
    | "voucher_free_delivery"
    | "free_product";
  name: string;
  picture_url?: string;
  code: string;
  validity_in_days: number;
  external_ids: readonly string[];
  is_usable: boolean;
  is_buyable: boolean;
  points_required?: number;
  offeredProducts?: readonly LoyaltyPreviewOfferedProduct[];
};

/** Serializable preview VM for `PongoAssetListBlockComponent`. */
export type PongoAssetListBlockScenarioContent = {
  customer: {
    points: number;
    rewards: readonly PongoAssetListPreviewAsset[];
  } | null;
};

export const PONGOASSETLISTBLOCK_LEGACY_FIELD_ORDER = [
  "heading",
  "subheading",
  { type: "separator" },
  "background_color",
  "heading_color",
  "text_color",
  "icon_color",
  { type: "separator" },
  "width",
  "items_to_show_card_carousel",
  { type: "separator" },
  "product_name_color",
  "product_description_color",
  "product_image_placeholder_color",
  "product_border_color",
  "product_background_color",
  "product_hover_colors.backgroundColor",
  "product_hover_colors.nameColor",
  "product_hover_colors.descriptionColor",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  pongoAssetListBlockDescriptor: _generatedPongoAssetListBlockDescriptor,
  pongoAssetListBlockBlocDefinition,
  pongoAssetListBlockDashboardLayout,
  pongoAssetListBlockConfig,
  pongoAssetListBlockResolveConfig,
  pongoAssetListBlockProperties,
  pongoAssetListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Pongo - Asset list",
    componentType: "PongoAssetListBlockComponent",
    category: "pongo",
    description: "Pongo - Asset list.",
    keywords: [],
    allowedIn: [{ bloc: "*Page", slot: "*" }],
    sharedFields: {},
  },

  () => ({
    heading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.pongo-asset-list.heading.label",
      },
    }),
    subheading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.pongo-asset-list.subheading.label",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-100" },
      options: {
        label: "pages.blocks.pongo-asset-list.background-color.label",
        help: "pages.blocks.pongo-asset-list.background-color.help",
      },
    }),
    heading_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.pongo-asset-list.heading-color.label",
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.pongo-asset-list.text-color.label",
        help: "pages.blocks.pongo-asset-list.text-color.help",
      },
    }),
    icon_color: propertyValue({
      type: "color",
      value: { type: "default", value: "" },
      options: {
        label: "pages.blocks.pongo-asset-list.icon-color.label",
        help: "pages.blocks.pongo-asset-list.icon-color.help",
        colorInheritFrom: "text_color",
      },
    }),
    width: propertyValue({
      type: "select",
      value: "centered",
      options: {
        label: "pages.blocks.pongo-asset-list.width.label",
        choices: [
          {
            value: "centered",
            label: "pages.blocks.pongo-asset-list.width.options.centered",
          },
          {
            value: "full",
            label: "pages.blocks.pongo-asset-list.width.options.full",
          },
        ],
      },
    }),
    items_to_show_card_carousel: propertyValue({
      type: "number",
      value: 4,
      options: {
        label:
          "pages.blocks.pongo-asset-list.items-to-show-card-carousel.label",
        help: "pages.blocks.pongo-asset-list.items-to-show-card-carousel.help",
        placeholder: "4",
      },
    }),
    product_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-900" },
      options: {
        label: "pages.blocks.pongo-asset-list.product-name-color.label",
      },
    }),
    product_description_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.pongo-asset-list.product-description-color.label",
        help: "pages.blocks.pongo-asset-list.product-description-color.help",
      },
    }),
    product_image_placeholder_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-50" },
      options: {
        label:
          "pages.blocks.pongo-asset-list.product-image-placeholder-color.label",
      },
    }),
    product_border_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-100" },
      options: {
        label: "pages.blocks.pongo-asset-list.product-border-color.label",
      },
    }),
    product_background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: {
        label: "pages.blocks.pongo-asset-list.product-background-color.label",
      },
    }),
    product_hover_colors: {
      backgroundColor: propertyValue({
        type: "color",
        value: { type: "palette", value: "neutral-50" },
        options: {
          label:
            "pages.blocks.pongo-asset-list.product-hover-colors.background-color.label",
          help: "pages.blocks.pongo-asset-list.product-hover-colors.help",
        },
      }),
      nameColor: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.pongo-asset-list.product-hover-colors.name-color.label",
          colorInheritFrom: "product_name_color",
        },
      }),
      descriptionColor: propertyValue({
        type: "color",
        value: { type: "default", value: "" },
        options: {
          label:
            "pages.blocks.pongo-asset-list.product-hover-colors.description-color.label",
          colorInheritFrom: "product_description_color",
        },
      }),
    },
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.pongo-asset-list.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.pongo-asset-list.fieldset.header.label",
            fields: [
              "heading",
              "subheading",
              "background_color",
              "heading_color",
              "text_color",
              "icon_color",
            ],
          },
          {
            legend: "pages.blocks.pongo-asset-list.fieldset.layout.label",
            fields: ["width", "items_to_show_card_carousel"],
          },
          {
            legend: "pages.blocks.pongo-asset-list.fieldset.product-cards.label",
            fields: [
              "product_name_color",
              "product_description_color",
              "product_image_placeholder_color",
              "product_border_color",
              "product_background_color",
            ],
          },
          {
            legend: "pages.blocks.pongo-asset-list.fieldset.hover.label",
            fields: [
              "product_hover_colors.backgroundColor",
              "product_hover_colors.nameColor",
              "product_hover_colors.descriptionColor",
            ],
          },
        ],
      },
    ],
  }),
);

export function pongoAssetListBlockDescriptor(): IBlockDescriptor;
export function pongoAssetListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function pongoAssetListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedPongoAssetListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedPongoAssetListBlockDescriptor,
    pongoAssetListBlockProperties,
    fieldOrder,
  );
}

export {
  pongoAssetListBlockBlocDefinition,
  pongoAssetListBlockDashboardLayout,
  pongoAssetListBlockProperties,
  pongoAssetListBlockConfig,
  pongoAssetListBlockResolveConfig,
  pongoAssetListBlockDefinitionSetupScenarios,
};

export type PongoAssetListBlockConfig = typeof pongoAssetListBlockConfig;
